import * as React from 'react'
import { useSpring, Interpolation, FrameValue } from 'react-spring'
import { useRefDep } from './ref-dep'

/**
 * Scroll offset interpolation alias.
 */
export type ScrollOffsetInterpolation = FrameValue<number>

/**
 * Gets the scroll offset on every tick but puts it in a spring to avoid rerenders.
 *
 * @param scrollRef
 */
export function useScrollOffsetSpring(
  scrollRef: React.RefObject<Element | null>,
): ScrollOffsetInterpolation {
  const [offset, springRef] = useSpring<{ offsetSpring: number }>(() => ({
    offsetSpring: 0,
    immediate: true,
  }))
  React.useEffect(() => {
    const $elem = scrollRef.current
    if (!$elem) {
      return
    }
    springRef.start({
      offsetSpring: $elem.scrollTop,
    })
    const handler = (e: Event) =>
      springRef.start({
        offsetSpring: (e.target as HTMLElement).scrollTop,
        immediate: true,
      })
    $elem.addEventListener('scroll', handler)
    return () => {
      $elem.removeEventListener('scroll', handler)
    }
  }, [useRefDep(scrollRef)])
  return offset.offsetSpring
}

/**
 * Used by components that want to allow an optional spring interpolation value.
 *
 * @param value the potential interpolation to use, if it's defined. If it's a plain number, will be converted to an interpolation.
 * @param defaultValue the default value. MUST be stable, don't change this between renders you fucking piece of trash.
 */
export function useFallbackNumberSpring(
  value: FrameValue<number> | number | undefined,
  defaultValue = 0,
): FrameValue<number> {
  const defaultValueSpring = useStaticOpaqueInterpolation(defaultValue)
  const isNumber = typeof value === 'number'
  const springJustInCaseTheInputWasANumberAndNotASpring =
    useStaticOpaqueInterpolation(isNumber ? value : 0)

  return isNumber
    ? springJustInCaseTheInputWasANumberAndNotASpring
    : (value ?? defaultValueSpring)
}

/**
 * Use a static opaque interpolation.
 * @param value
 */
function useStaticOpaqueInterpolation<T>(value: T): Interpolation<T> {
  return React.useMemo(() => createStaticOpaqueInterpolation(value), [value])
}

// The zero value.
const ZERO = _createStaticOpaqueInterpolation(0) as Interpolation<number>

/**
 * Creates a static opaque interpolation, uses the ZERO instance if the value is 0.
 * @param value
 */
function createStaticOpaqueInterpolation<T>(value: T): Interpolation<T> {
  // Optimization so we don't create a bunch of garbage for fallback springs.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (value === 0) {
    return ZERO as any
  }
  return _createStaticOpaqueInterpolation(value)
}

/**
 * Creates a static opaque interpolation.
 * @param value
 */
function _createStaticOpaqueInterpolation<T>(value: T): Interpolation<T> {
  const interpolation: Interpolation<T> = {
    get: () => value,
    to: <U>(fn: (v: T) => U) =>
      typeof fn === 'function'
        ? createStaticOpaqueInterpolation(fn(value))
        : interpolation,
  } as Interpolation<T>
  return interpolation
}
